"use client";

import { useRef, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";

import { CandidateAvatar } from "@/components/ui/candidate-avatar";
import { Icon } from "@/components/ui/icon";
import { Select } from "@/components/ui/select";
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll";
import { listPipeline } from "@/lib/api/generated";
import type { PipelineCandidateResponse } from "@/lib/api/model/pipelineCandidateResponse";

const PER_PAGE = 20;

function displayName(name: string | null | undefined): string {
  return name || "Unnamed";
}

type ProfileInputProps = {
  jobId: string | null;
  value: string;
  onChange: (candidate: PipelineCandidateResponse) => void;
  placeholder?: string;
  /**
   * The currently-selected candidate object. Lets the trigger render the
   * name + avatar when the selection was set externally (e.g. an
   * auto-selected first candidate) rather than picked from the dropdown.
   */
  selectedCandidate?: PipelineCandidateResponse | null;
  /**
   * Multi-select behaviour: picking a candidate keeps the dropdown open
   * so several profiles can be added in one pass — clicking outside (or
   * Esc) closes it. Used by the task-create "Add Profile(s)" picker.
   */
  keepOpenOnSelect?: boolean;
  /**
   * Ids to render with a selected check even though `value` is a single
   * id — pairs with `keepOpenOnSelect` so already-added profiles are
   * visible in the list.
   */
  selectedIds?: string[];
} & Omit<
  React.ComponentProps<typeof Select.Trigger>,
  "value" | "onChange" | "children"
>;

export function ProfileInput({
  jobId,
  value,
  onChange,
  placeholder = "Select Profile",
  selectedCandidate,
  keepOpenOnSelect = false,
  selectedIds,
  className,
  variant,
  disabled,
  ...triggerProps
}: ProfileInputProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pickedCandidate, setPickedCandidate] =
    useState<PipelineCandidateResponse | null>(null);

  const query = useInfiniteQuery({
    queryKey: ["pipeline-picker", jobId, search.trim()],
    queryFn: ({ pageParam }) =>
      listPipeline(jobId ?? "", {
        per_page: PER_PAGE,
        search: search.trim() || undefined,
        // Empty string opts the backend into cursor mode for the first page.
        cursor: pageParam,
      }),
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled: open && !!jobId,
  });

  const viewportRef = useRef<HTMLDivElement>(null);
  useInfiniteScroll({
    rootRef: viewportRef,
    hasNextPage: query.hasNextPage,
    isFetching: query.isFetchingNextPage,
    onLoadMore: query.fetchNextPage,
    enabled: open,
  });

  const candidates =
    query.data?.pages.flatMap((p) => p.candidates) ?? [];
  // Resolve the active candidate from the in-dropdown pick first, then the
  // externally-supplied selection — so an auto-selected candidate still
  // renders its name + avatar in the trigger.
  const activeCandidate =
    pickedCandidate?.profile_id === value
      ? pickedCandidate
      : selectedCandidate?.profile_id === value
        ? selectedCandidate
        : null;
  const label = activeCandidate
    ? displayName(activeCandidate.full_name)
    : null;
  const isDisabled = disabled || !jobId;

  return (
    <Select.Root
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) setSearch("");
      }}
    >
      <Select.Trigger
        variant={variant}
        className={className}
        active={!!value}
        disabled={isDisabled}
        {...triggerProps}
      >
        {activeCandidate ? (
          <CandidateAvatar
            name={displayName(activeCandidate.full_name)}
            avatarUrl={activeCandidate.avatar_url ?? undefined}
            size={16}
            initialsPx={8}
          />
        ) : (
          <Icon name="IconPeopleCircle" size={14} className="shrink-0" />
        )}
        <Select.TriggerContent>
          {label || placeholder}
        </Select.TriggerContent>
        <Select.Icon className="ml-auto" />
      </Select.Trigger>

      <Select.Content
        matchTriggerWidth={false}
        className="w-[380px] min-w-[320px]"
        align="start"
      >
        <Select.Search
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search profiles…"
          autoFocus
        />
        <Select.List viewportRef={viewportRef}>
          {query.isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Icon
                name="IconLoadingCircle"
                size={18}
                className="animate-spin text-contrast/40"
              />
            </div>
          ) : candidates.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-0.5 px-3 py-6">
              <p className="text-sm font-medium text-heading">
                No profiles found
              </p>
              <p className="mt-0.5 text-center text-xs leading-normal text-contrast/80">
                {search.trim()
                  ? "No matching profiles. Try a different search."
                  : "This job has no profiles yet."}
              </p>
            </div>
          ) : (
            <>
              {candidates.map((cand, i) => {
                const isSelected =
                  cand.profile_id === value ||
                  (selectedIds?.includes(cand.profile_id) ?? false);
                return (
                  <Select.Item
                    key={cand.profile_id}
                    index={i}
                    selected={isSelected}
                    onSelect={() => {
                      setPickedCandidate(cand);
                      onChange(cand);
                      if (!keepOpenOnSelect) setOpen(false);
                    }}
                  >
                    <Select.ItemIcon>
                      <CandidateAvatar
                        name={displayName(cand.full_name)}
                        avatarUrl={cand.avatar_url ?? undefined}
                        size={20}
                        initialsPx={9}
                      />
                    </Select.ItemIcon>
                    <Select.ItemLabel selected={isSelected}>
                      {displayName(cand.full_name)}
                    </Select.ItemLabel>
                    {isSelected && <Select.ItemCheck />}
                  </Select.Item>
                );
              })}
              {query.isFetchingNextPage && (
                <div className="flex items-center justify-center py-2">
                  <Icon
                    name="IconLoadingCircle"
                    size={16}
                    className="animate-spin text-contrast/40"
                  />
                </div>
              )}
            </>
          )}
        </Select.List>
      </Select.Content>
    </Select.Root>
  );
}
